/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
    getRentalItemById,
    uploadRentalGalleryImages,
    deleteRentalGalleryImage,
    setRentalPrimaryImage
} from "../../../../services/rentalService";
import {
    formatGalleryUploadError,
    validateGalleryFiles,
    GALLERY_FORMATS_LABEL,
} from "../../../../utils/galleryUploadErrors";

const RentalGallery = ({ rentalId, onValidityChange }) => {
    const queryClient = useQueryClient();
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState([]);

    const { data: rentalData, isLoading } = useQuery({
        queryKey: ["rentalItem", rentalId],
        queryFn: () => getRentalItemById(rentalId),
        enabled: !!rentalId,
    });

    const images = rentalData?.data?.gallery_images || [];

    useEffect(() => {
        onValidityChange?.(true); 
    }, [images]);

    const uploadMutation = useMutation({
        mutationFn: (formData) => uploadRentalGalleryImages(rentalId, formData),
        onSuccess: () => {
            queryClient.invalidateQueries(["rentalItem", rentalId]);
            setUploading(false);
            setError([]);
        },
        onError: (e) => {
            setUploading(false);
            setError(formatGalleryUploadError(e));
        }
    });

    const deleteMutation = useMutation({
        mutationFn: deleteRentalGalleryImage,
        onSuccess: () => {
            queryClient.invalidateQueries(["rentalItem", rentalId]);
        }
    });

    const setPrimaryMutation = useMutation({
        mutationFn: setRentalPrimaryImage,
        onSuccess: () => {
            queryClient.invalidateQueries(["rentalItem", rentalId]);
        }
    });

    const handleImageChange = async (files) => {
        if (!files || files.length === 0) return;

        const clientErrors = validateGalleryFiles(files);
        if (clientErrors.length > 0) {
            setError(clientErrors);
            return;
        }

        setUploading(true);
        setError([]);
        const formData = new FormData();
        if (Array.isArray(files)) {
            files.forEach((file) => formData.append("images[]", file));
        } else {
            formData.append("images[]", files);
        }
        uploadMutation.mutate(formData);
    };

    if (isLoading) return <div className="p-10 text-center text-gray-500">Loading gallery...</div>;

    return (
        <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                    {/* Upload Card */}
                    <div className="aspect-[4/3] relative">
                         <div className={`h-full w-full border-2 border-dashed border-gray-200 rounded-2xl hover:border-primary hover:bg-primary/5 transition-all duration-300 flex flex-col items-center justify-center cursor-pointer group ${uploading ? 'opacity-50 pointer-events-none' : ''}`}>
                             <input
                                 type="file"
                                 accept="image/jpeg,image/png,image/webp,image/avif,image/avif-sequence,.avif,.avifs"
                                 multiple
                                 disabled={uploading}
                                 className="absolute inset-0 opacity-0 z-10 cursor-pointer"
                                 onChange={(e) => {
                                     const selected = e?.target?.files ? Array.from(e.target.files) : [];
                                     // allow selecting the same file again
                                     e.target.value = "";
                                     handleImageChange(selected);
                                 }}
                             />
                             <div className="flex flex-col items-center gap-2 text-gray-400 group-hover:text-primary transition-colors">
                                <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center group-hover:bg-primary/10">
                                    {uploading ? (
                                        <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                                    ) : (
                                        <i className="fi fi-rr-plus flex items-center justify-center text-lg"></i>
                                    )}
                                </div>
                                <span className="text-xs font-medium text-gray-500">{uploading ? "Uploading..." : "Add photos"}</span>
                                <span className="text-[10px] text-gray-400">{GALLERY_FORMATS_LABEL} · max 10 MB each</span>
                             </div>
                         </div>
                    </div>

                    {/* Image Cards */}
                    {images.map((img) => (
                        <div key={img.id} className="aspect-[4/3] relative group rounded-2xl overflow-hidden border border-gray-100 shadow-sm bg-gray-50">
                            <img 
                                src={img.image_url} 
                                alt="Vehicle" 
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            
                            {/* Overlay */}
                            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-between p-3">
                                <div className="flex justify-end">
                                    <button 
                                        onClick={() => deleteMutation.mutate(img.id)}
                                        className="w-8 h-8 rounded-lg bg-red-500/90 text-white flex items-center justify-center hover:bg-red-600 transition-colors shadow-lg"
                                        title="Delete Image"
                                    >
                                        <i className="fi fi-rr-trash text-sm"></i>
                                    </button>
                                </div>
                                <div className="flex justify-center">
                                    <button 
                                        onClick={() => setPrimaryMutation.mutate(img.id)}
                                        disabled={img.is_primary}
                                        className={`px-4 py-1.5 rounded-full text-[10px] font-bold uppercase tracking-wider transition-all duration-300 shadow-lg
                                            ${img.is_primary 
                                                ? "bg-green-500 text-white border-green-500" 
                                                : "bg-white/90 text-gray-900 hover:bg-white active:scale-95"}`}
                                    >
                                        {img.is_primary ? "✓ Primary Image" : "Set as Primary"}
                                    </button>
                                </div>
                            </div>

                            {/* Info Badge */}
                            {img.is_primary && (
                                <div className="absolute top-3 left-3 px-3 py-1 bg-green-500/90 text-white text-[10px] font-bold uppercase tracking-wider rounded-full shadow-lg backdrop-blur-sm group-hover:hidden">
                                     Primary
                                </div>
                            )}
                        </div>
                    ))}
                </div>

            {error.length > 0 ? (
                <div className="p-3 bg-red-50 border border-red-100 rounded-xl text-sm text-red-700">
                    {error.length === 1 ? (
                        <p>{error[0]}</p>
                    ) : (
                        <ul className="list-disc pl-4 space-y-1">
                            {error.map((message) => (
                                <li key={message}>{message}</li>
                            ))}
                        </ul>
                    )}
                </div>
            ) : null}

            <div className="p-4 bg-blue-50 border border-blue-100 rounded-xl flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
                    <i className="fi fi-rr-bulb text-sm"></i>
                </div>
                <div>
                    <p className="text-xs font-medium text-blue-900 mb-1">Photography tips</p>
                    <p className="text-xs text-blue-700 leading-relaxed">
                        Upload clear, bright photos from multiple angles (interior, exterior, trunk). High-quality photos significantly increase conversion rates.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default RentalGallery;
